import React, { memo } from 'react';
import propTypes from 'prop-types';
import { Columns, Column } from 'src/ui/mui-ui-kit';
import { Field } from 'react-final-form';
import { cleanseDecimal } from 'src/ui/util';
import { UiInputAdapter, UiToggleAdapter, UiFieldset } from 'src/ui';
import { composeValidators, required } from 'src/ui/validations';

const Condition = ({ when, is, children }) => {
  return when === is ? children : '';
};

function LoanDeclarationsNotificationsForm({ className, form, values }) {
  return (
    <>
      <UiFieldset title="Declarations" className={className}>
        <Columns isMultiline>
          <Column isSize="full">
            <Field
              component={UiToggleAdapter}
              name="loanDecApplicantOblig"
              id="loanDecApplicantOblig"
              type="checkbox"
            >
              Is the applicant or Owner an Endorser, Guarantor, or Co-Maker for
              obligations (including any lease obligation, e.g. vehicle,
              equipment, lease of business location) not listed on its financial
              statements?
            </Field>
          </Column>
          <Column isSize="full">
            <Field
              component={UiToggleAdapter}
              name="loanDecApplicantTaxesPastDue"
              id="loanDecApplicantTaxesPastDue"
              type="checkbox"
            >
              Are any of the applicant&apos;s or any proposed
              Owner&apos;s/Guarantor&apos;s taxes past due?
            </Field>
          </Column>

          <Condition when={values.loanDecApplicantTaxesPastDue} is>
            <Column isSize="1/3" className="is-one-third-fullhd">
              <Field
                component={UiInputAdapter}
                name="loanDecApplicantTaxesPastDueAmt"
                id="loanDecApplicantTaxesPastDueAmt"
                label="Amount $"
                validate={composeValidators('Amount $', required)}
                onChange={e => form.mutators.cleanse(e, cleanseDecimal)}
                required
              />
            </Column>
            <Column isSize="1/3" className="is-one-third-fullhd">
              <Field
                component={UiInputAdapter}
                name="loanDecApplicantTaxesPastDueOwedTo"
                id="loanDecApplicantTaxesPastDueOwedTo"
                label="Owed to"
                validate={composeValidators('Owed to', required)}
                required
              />
            </Column>
          </Condition>

          <Column isSize="full">
            <Field
              component={UiToggleAdapter}
              name="loanDecOtherAssetsPledged"
              id="loanDecOtherAssetsPledged"
              type="checkbox"
            >
              Are there any assets pledged or mortgaged other than as stated on
              the business and personal financial statements submitted?
            </Field>
          </Column>
          <Column isSize="full">
            <Field
              component={UiToggleAdapter}
              name="loanDecOtherCreditPastDue"
              id="loanDecOtherCreditPastDue"
              type="checkbox"
            >
              Are any of the Applicant&apos;s or any proposed
              Owner&apos;s/Guarantor&apos;s credit obligations past due?
            </Field>
          </Column>
          <Column isSize="full">
            <Field
              component={UiToggleAdapter}
              name="loanDecApplicantFelon"
              id="loanDecApplicantFelon"
              type="checkbox"
            >
              Are any of the proposed Owners/guarantors presently under
              indictment, on probation or parole or convicted for any criminal
              offense other than a minor motor vehicle violation?
            </Field>
          </Column>
          <Column isSize="full">
            <Field
              component={UiToggleAdapter}
              name="loanDecApplicantPolitical"
              id="loanDecApplicantPolitical"
              type="checkbox"
            >
              Is the Applicant/Owner/Guarantor, a political party, a campaign, a
              candidate, a public official or foreign political official or an
              immediate family member of such an official, or a business entity
              formed by or for the benefit of any public official?
            </Field>
          </Column>

          <Condition when={values.loanDecApplicantPolitical} is>
            <Column isSize="1/3" className="is-one-third-fullhd">
              <Field
                component={UiInputAdapter}
                name="loanDecApplicantPoliticalNameOfEmployee"
                id="loanDecApplicantPoliticalNameOfEmployee"
                label="Name, relationship, and position of offical"
                validate={composeValidators(
                  'Name, relationship, and position of offical',
                  required
                )}
                required
              />
            </Column>
          </Condition>

          <Column isSize="full">
            <Field
              component={UiToggleAdapter}
              name="loanDecApplicantEmployeeOfBank"
              id="loanDecApplicantEmployeeOfBank"
              type="checkbox"
            >
              Is the applicant or Owner an employee of the Bank?
            </Field>
          </Column>

          <Condition when={values.loanDecApplicantEmployeeOfBank} is>
            <Column isSize="1/3" className="is-one-third-fullhd">
              <Field
                component={UiInputAdapter}
                name="loanDecApplicantEmployeeOfBankEmployee"
                id="loanDecApplicantEmployeeOfBankEmployee"
                label="Name of employee"
                validate={composeValidators('Name of employee', required)}
                required
              />
            </Column>
          </Condition>

          <Column isSize="full">
            <Field
              component={UiToggleAdapter}
              name="loanDecApplicantFinInstEmployee"
              id="loanDecApplicantFinInstEmployee"
              type="checkbox"
            >
              Is the Applicant or Owner an officer/employee/director of any
              financial institution?
            </Field>
          </Column>

          <Condition when={values.loanDecApplicantFinInstEmployee} is>
            <Column isSize="1/3" className="is-one-third-fullhd">
              <Field
                component={UiInputAdapter}
                name="loanDecApplicantFinInstEmployeeFi"
                id="loanDecApplicantFinInstEmployeeFi"
                label="Financial Institution"
                validate={composeValidators('Financial Institution', required)}
                required
              />
            </Column>
          </Condition>

          <Column isSize="full">
            <Field
              component={UiToggleAdapter}
              name="loanDecWillFiftyComeFromLease"
              id="loanDecWillFiftyComeFromLease"
              type="checkbox"
            >
              Will 50% or more of the repayment come from lease payments from
              tenants that are not affiliated with the applicant?
            </Field>
          </Column>
          <Column isSize="full">
            <Field
              component={UiToggleAdapter}
              name="loanDecWillFiftyComeFromSale"
              id="loanDecWillFiftyComeFromSale"
              type="checkbox"
            >
              Will 50% or more of the repayment come from the sale of some or
              all of the financed property to a third party not affiliated with
              the Applicant?
            </Field>
          </Column>
        </Columns>
      </UiFieldset>

      <UiFieldset title="Notifications" className={className}>
        <Columns isMultiline>
          <Column isSize="full" className="is-full">
            <div className="label">
              - If the credit request is for a commercial mortgage or term loan
              and is HMDA reportable, a HMDA worksheet must be completed and
              submitted with this application
            </div>
          </Column>
          <Column isSize="full" className="is-full">
            <br />
          </Column>
          <Column isSize="full" className="is-full">
            <div className="label">
              - Applicants with Gross Revenue less than $1,000,000:
            </div>
            <div className="label">
              I (“Applicant”) understand that if this credit application is
              denied I may request a written statement of the specific reasons
              for the denial. To obtain the statement, I must contact CFG
              Community Bank, 1422 Clarview Road, Baltimore, MD 21229 or call
              410-823-0500 within 60 days from the date I am notified of the
              denial. CFG Community Bank will then send me a written statement
              of reasons for the denial within 30 days of receiving my request
              for such statement. NOTICE: The Federal Equal Credit Opportunity
              Act prohibits creditors from discriminating against credit
              applications on the basis of race, color, religion, national
              origin, sex, marital status, age (provided the applicant has the
              capacity to enter into a binding contract); because all or part of
              the applicants’s income derives from any public assistance
              program, or because the applicant has in good faith exercised any
              right under the Consumer Credit Protection Act. The federal agency
              that administers compliance with this law concerning CFG Community
              Bank is the Federal Reserve Consumer Help Center, P.O. Box 1200,
              Minneapolis, MN 55480.
            </div>
          </Column>
        </Columns>
      </UiFieldset>
    </>
  );
}

LoanDeclarationsNotificationsForm.defaultProps = {
  className: '',
};

LoanDeclarationsNotificationsForm.propTypes = {
  className: propTypes.string,
  form: propTypes.object.isRequired,
  values: propTypes.object.isRequired,
};

const memoizedLoanDeclarationsNotificationsForm = memo(
  LoanDeclarationsNotificationsForm
);

export { memoizedLoanDeclarationsNotificationsForm as LoanDeclarationsNotificationsForm };
